import { KEM_SOCKET_HOST } from "@ch/api";
import { StorageNamesEnum } from "@ch/helpers/utils/enums";
import localforage from "localforage";
import normalizeKemProgress from "./normalizeKemProgress";

const log = (...args: unknown[]) =>
  // eslint-disable-next-line no-console
  process.env.NODE_ENV !== "production" && console.log(...args);
const logError = (...args: unknown[]) =>
  // eslint-disable-next-line no-console
  process.env.NODE_ENV !== "production" && console.error(...args);

export type KemCoachSocketState =
  | "DISCONNECTED"
  | "CONNECTING"
  | "CONNECTED"
  | "READY"
  | "STREAMING"
  | "COMPLETE"
  | "ERROR"
  | "RECONNECTING";

export interface KemCoachMemberPayload {
  gender?: string;
  date_of_birth?: string;
  latest_bmi?: number;
  bmi_measurement_date?: string;
  latest_bp?: string;
  bp_measurement_date?: string;
  pregnancy_status?: boolean;
  pregnancy_edd?: string;
  exercise_duration_last_30_days?: string;
  latest_blood_sugar?: string;
  blood_sugar_measurement_date?: string;
  latest_blood_sugar_meal_type?: string;
  latest_cholesterol?: string;
  cholesterol_measurement_date?: string;
  latest_weight?: string;
  weight_measurement_date?: string;
  latest_weight_goal?: string;
  latest_height?: string;
  height_measurement_date?: string;
  latest_waist_circumference?: string;
  waist_circumference_measurement_date?: string;
  waist_circumference_goal?: string;
  latest_hbaic?: string;
  hbaic_measurement_date?: string;
  latest_total_psa?: string;
  latest_free_psa?: string;
  latest_psa_file_name?: string;
  psa_measurement_date?: string;
  latest_eye_examination?: string;
  eye_examination_measurement_date?: string;
  latest_foot_examination?: string;
  foot_examination_measurement_date?: string;
  latest_health_condition?: string;
  health_condition_measurement_date?: string;
  latest_procedure_surgeries?: string;
  procedure_surgeries_measurement_date?: string;
  lastest_prescription?: string;
  prescription_measurement_date?: string;
  current_week?: string;
  program_id?: string;
  input_audio_url?: string;
  user_id?: string;
  session_key?: string;
  coach_details?: {
    coach_id?: string;
    coach_name?: string;
  }[];
  macro_goals?: string[];
}

export interface KemCoachCompletionData {
  output_audio_url?: string;
  kem_message_id?: string;
  user_message_id?: string;
  infographic?: string;
  infographic_messageid?: string;
  session_completed?: boolean;
  session_key?: string;
  completion_progress?: number;
  options?: {
    label: string;
    value: string;
  }[];
}

export interface KemCoachSocketCallbacks {
  onStateChange?: (state: KemCoachSocketState) => void;
  onChunk?: (chunk: string) => void;
  onComplete?: (data: KemCoachCompletionData) => void;
  onError?: (error: string) => void;
  onMemberAck?: () => void;
}

class KemCoachSocket {
  private ws: WebSocket | null = null;
  private callbacks: KemCoachSocketCallbacks = {};
  private state: KemCoachSocketState = "DISCONNECTED";
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private isIntentionallyClosed = false;
  private expectingMetadata = false;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private static getWebSocketUrl(): string {
    const socketHost = String(KEM_SOCKET_HOST);
    const baseUrl = socketHost.replace(/^https?:\/\//, "");
    const protocol = socketHost.startsWith("https") ? "wss" : "ws";
    return `${protocol}://${baseUrl}/ws/chat`;
  }

  private setState(newState: KemCoachSocketState) {
    this.state = newState;
    this.callbacks.onStateChange?.(newState);
  }

  private calculateReconnectDelay(): number {
    return Math.min(
      this.baseReconnectDelay * 2 ** (this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );
  }

  private static async getRefreshToken(): Promise<string | null> {
    try {
      const token = await localforage.getItem<string>(
        StorageNamesEnum.REFRESH_TOKEN,
      );
      return token ?? null;
    } catch (error) {
      logError("[KemCoachSocket] Failed to get refresh token:", error);
      return null;
    }
  }

  async connect(callbacks: KemCoachSocketCallbacks): Promise<void> {
    if (this.state === "CONNECTING" || this.state === "CONNECTED") {
      log("[KemCoachSocket] Already connecting/connected, skipping");
      return;
    }

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.callbacks = callbacks;
    this.isIntentionallyClosed = false;

    try {
      const refreshToken = await KemCoachSocket.getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token found");
      }

      this.setState("CONNECTING");

      const url = `${KemCoachSocket.getWebSocketUrl()}?x_token=${refreshToken}`;
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        log("[KemCoachSocket] WebSocket connected");
        this.setState("CONNECTED");
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event: MessageEvent) => {
        log("[KemCoachSocket] WebSocket message received", event.data);
        this.handleMessage(String(event.data));
      };

      this.ws.onerror = (error: Event) => {
        log("[KemCoachSocket] WebSocket error", error);
      };

      this.ws.onclose = (event: CloseEvent) => {
        log("[KemCoachSocket] WebSocket closed", event.code, event.reason);

        if (this.isIntentionallyClosed) {
          log("[KemCoachSocket] Intentionally closed, not reconnecting");
          this.setState("DISCONNECTED");
          return;
        }

        if (event.code === 1008 || event.reason === "unauthorized") {
          log("[KemCoachSocket] Auth error, not reconnecting");
          this.setState("ERROR");
          this.callbacks.onError?.("unauthorized");
          return;
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts += 1;
          const delay = this.calculateReconnectDelay();

          log(
            `[KemCoachSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
          );

          this.setState("RECONNECTING");

          if (this.reconnectTimeoutId) clearTimeout(this.reconnectTimeoutId);

          this.reconnectTimeoutId = setTimeout(() => {
            log("[KemCoachSocket] Attempting reconnection...");
            void this.connect(this.callbacks);
          }, delay);
        } else {
          log("[KemCoachSocket] Max reconnection attempts reached, giving up");
          this.setState("ERROR");
          this.callbacks.onError?.(
            "Connection lost. Please check your internet connection.",
          );
        }
      };
    } catch (error) {
      this.setState("ERROR");
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      this.callbacks.onError?.(errorMessage);
    }
  }

  private handleMessage(data: string) {
    if (data === "member-payload:ok") {
      this.setState("READY");
      this.callbacks.onMemberAck?.();
      return;
    }

    if (data === "error:400-unauthorized") {
      this.setState("ERROR");
      this.callbacks.onError?.("unauthorized");
      return;
    }

    if (this.expectingMetadata && data.startsWith("{")) {
      this.expectingMetadata = false;
      this.setState("COMPLETE");
      this.parseCompletionData(data);
      return;
    }

    if (data.startsWith("chat-output:")) {
      const content = data.substring("chat-output:".length);

      if (content.trim() === "response_complete") {
        this.expectingMetadata = true;
        return;
      }

      this.callbacks.onChunk?.(content);

      if (this.state !== "STREAMING") {
        this.setState("STREAMING");
      }
    }
  }

  private static parseQuickOptions(
    raw: unknown,
  ): KemCoachCompletionData["options"] {
    if (!Array.isArray(raw)) {
      return undefined;
    }

    const options = raw
      .map((item) => {
        if (!item || typeof item !== "object") {
          return undefined;
        }

        const option = item as Record<string, unknown>;
        const label =
          typeof option.label === "string" ? option.label.trim() : "";
        const value =
          typeof option.value === "string" ? option.value.trim() : "";

        if (!label || !value) {
          return undefined;
        }

        return { label, value };
      })
      .filter((option): option is { label: string; value: string } =>
        Boolean(option),
      );

    return options.length > 0 ? options : undefined;
  }

  private parseCompletionData(data: string) {
    try {
      const parsedData = JSON.parse(data) as Record<string, unknown>;

      const rawSessionCompleted =
        parsedData.session_completed ?? parsedData.sessionCompleted;

      const sessionCompleted =
        typeof rawSessionCompleted === "boolean"
          ? rawSessionCompleted
          : typeof rawSessionCompleted === "number"
            ? rawSessionCompleted === 1
            : typeof rawSessionCompleted === "string"
              ? ["true", "1", "yes"].includes(
                  rawSessionCompleted.trim().toLowerCase(),
                )
              : undefined;

      const completionData: KemCoachCompletionData = {
        output_audio_url:
          typeof parsedData.output_audio_url === "string"
            ? parsedData.output_audio_url
            : undefined,
        kem_message_id:
          typeof parsedData.kem_message_id === "string"
            ? parsedData.kem_message_id
            : undefined,
        user_message_id:
          typeof parsedData.user_message_id === "string"
            ? parsedData.user_message_id
            : undefined,
        infographic:
          typeof parsedData.infographic === "string"
            ? parsedData.infographic
            : undefined,
        infographic_messageid:
          typeof parsedData.infographic_messageid === "string"
            ? parsedData.infographic_messageid
            : undefined,
        session_completed: sessionCompleted,
        session_key:
          typeof parsedData.session_key === "string"
            ? parsedData.session_key
            : undefined,
        completion_progress:
          typeof parsedData.completion_progress === "number" ||
          typeof parsedData.completion_progress === "string"
            ? normalizeKemProgress(parsedData.completion_progress)
            : undefined,
        options: KemCoachSocket.parseQuickOptions(parsedData.options),
      };

      this.callbacks.onComplete?.(completionData);
    } catch (error) {
      logError("[KemCoachSocket] Error parsing completion data:", error);
      this.callbacks.onComplete?.({});
    }
  }

  sendMemberPayload(payload: KemCoachMemberPayload): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError?.("WebSocket is not connected");
      return false;
    }

    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== null && v !== undefined),
    );

    log("[KemCoachSocket] Sending member payload:", cleanedPayload);

    this.ws.send(`member-payload:${JSON.stringify(cleanedPayload)}`);
    return true;
  }

  sendChatMessage(message: string): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError?.("WebSocket is not connected");
      return false;
    }

    if (this.state === "STREAMING") {
      this.callbacks.onError?.("Cannot send message while streaming");
      return false;
    }

    this.expectingMetadata = false;
    this.ws.send(`chat-incoming:${message}`);
    return true;
  }

  disconnect() {
    log("[KemCoachSocket] Disconnecting...");
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = 0;
    this.expectingMetadata = false;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setState("DISCONNECTED");
  }

  reconnect() {
    log("[KemCoachSocket] Manual reconnection requested");
    this.reconnectAttempts = 0;
    this.isIntentionallyClosed = false;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    void this.connect(this.callbacks);
  }

  getState(): KemCoachSocketState {
    return this.state;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export default KemCoachSocket;
